import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-alpine.css";
import { SquarePen, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "react-toastify";
import ListView from "../../shared/ListView";
import ConfirmModal from "../../shared/ConfirmModal";

const RolePrivilege = () => {
  const navigate = useNavigate();
  const gridRef = useRef(null);

  const [rowData, setRowData] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const [deleteId, setDeleteId] = useState(null);
  const [deleteMode, setDeleteMode] = useState("single");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedNames, setSelectedNames] = useState([]);
  const selectedIdsRef = useRef([]);
  const [allSelected, setAllSelected] = useState(false);
  const [preserveSelection, setPreserveSelection] = useState(false);

  // Filter and Sort states
  const [filterColumn, setFilterColumn] = useState("roleName");
  const [filterValue, setFilterValue] = useState("");
  const [columnOptions, setColumnOptions] = useState([]);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  useEffect(() => {
    const options = [
      { value: "roleName", label: "Role Name" },
      { value: "menuName", label: "Menu Name" },
      { value: "view", label: "View Permission" },
      { value: "add", label: "Add Permission" },
      { value: "edit", label: "Edit Permission" },
      { value: "delete", label: "Delete Permission" },
    ];
    setColumnOptions(options);
    fetchRolePrivilegeData();
  }, []);

  const fetchRolePrivilegeData = async () => {
    try {
      setLoading(true);
      // Mock data - replace with actual API call when available
      const mockData = [
        {
          privilegeId: 1,
          roleName: "Data Entry",
          menuName: "Users",
          view: true,
          add: true,
          edit: false,
          delete: false,
        },
        {
          privilegeId: 2,
          roleName: "Data Reader",
          menuName: "Products",
          view: true,
          add: false,
          edit: false,
          delete: false,
        },
        {
          privilegeId: 3,
          roleName: "Admin",
          menuName: "Branch Management",
          view: true,
          add: true,
          edit: true,
          delete: true,
        },
        {
          privilegeId: 4,
          roleName: "Manager",
          menuName: "Customer Management",
          view: true,
          add: true,
          edit: true,
          delete: false,
        },
        {
          privilegeId: 5,
          roleName: "User",
          menuName: "Reports",
          view: true,
          add: false,
          edit: false,
          delete: false,
        },
      ];

      // Apply filtering if needed
      let filteredData = mockData;
      if (isFilterApplied && filterValue.trim()) {
        filteredData = mockData.filter(item => {
          const value = item[filterColumn]?.toString().toLowerCase();
          return value?.includes(filterValue.toLowerCase());
        });
      }

      // Apply sorting if needed
      if (sortField && sortDirection) {
        filteredData.sort((a, b) => {
          const aVal = a[sortField];
          const bVal = b[sortField];
          if (sortDirection === "asc") {
            return aVal > bVal ? 1 : -1;
          } else {
            return aVal < bVal ? 1 : -1;
          }
        });
      }

      setRowData(filteredData);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch role privilege data");
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = async () => {
    if (!filterValue.trim()) {
      toast.warning("Please enter filter value");
      return;
    }
    setIsFilterApplied(true);
    setCurrentPage(1);
    await fetchRolePrivilegeData();
  };

  const clearFilters = async () => {
    setFilterValue("");
    setIsFilterApplied(false);
    setCurrentPage(1);
    await fetchRolePrivilegeData();
  };

  const handleSort = async (field) => {
    let direction = "asc";
    if (sortField === field) {
      direction = sortDirection === "asc" ? "desc" : sortDirection === "desc" ? null : "asc";
    }
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1);

    if (!direction) {
      setSortField(null);
      setSortDirection(null);
    }
    await fetchRolePrivilegeData();
  };

  const createHeaderComponent = (field, displayName) => {
    return () => {
      const isSorted = sortField === field;
      const isAsc = sortDirection === "asc";

      return (
        <div
          className="cursor-pointer hover:text-brandRed flex items-center"
          onClick={() => handleSort(field)}
        >
          {displayName}
          {isSorted && (
            <span className="ml-1">
              {isAsc ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </span>
          )}
        </div>
      );
    };
  };

  const [columnDefs, setColumnDefs] = useState([]);

  useEffect(() => {
    const createColumnDefs = () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 50,
        suppressMenu: true,
      },
      {
        headerName: "Role Name",
        field: "roleName",
        flex: 1,
        headerComponent: createHeaderComponent("roleName", "Role Name"),
        suppressMenu: true,
      },
      {
        headerName: "Menu Name",
        field: "menuName",
        flex: 1,
        headerComponent: createHeaderComponent("menuName", "Menu Name"),
        suppressMenu: true,
      },
      {
        headerName: "View",
        field: "view",
        flex: 1,
        headerComponent: createHeaderComponent("view", "View"),
        cellRenderer: (params) => (
          <div className="text-center">
            {params.value ? "✅" : "❌"}
          </div>
        ),
        suppressMenu: true,
      },
      {
        headerName: "Add",
        field: "add",
        flex: 1,
        headerComponent: createHeaderComponent("add", "Add"),
        cellRenderer: (params) => (
          <div className="text-center">
            {params.value ? "✅" : "❌"}
          </div>
        ),
        suppressMenu: true,
      },
      {
        headerName: "Edit",
        field: "edit",
        flex: 1,
        headerComponent: createHeaderComponent("edit", "Edit"),
        cellRenderer: (params) => (
          <div className="text-center">
            {params.value ? "✅" : "❌"}
          </div>
        ),
        suppressMenu: true,
      },
      {
        headerName: "Delete",
        field: "delete",
        flex: 1,
        headerComponent: createHeaderComponent("delete", "Delete"),
        cellRenderer: (params) => (
          <div className="text-center">
            {params.value ? "✅" : "❌"}
          </div>
        ),
        suppressMenu: true,
      },
      {
        headerName: "Action",
        field: "action",
        headerClass: 'action-header',
        cellRenderer: (params) => (
          <div className="flex space-x-2 mt-1">
            <button
              className="edit-button"
              onClick={() => navigate(`/role-privilege/edit/${params.data.privilegeId}`)}
              title="Edit"
              onMouseDown={(e) => e.preventDefault()}
            >
              <SquarePen color="#787474" size={16} className="mt-1" />
            </button>
            <button
              className="delete-button"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteId(params.data.privilegeId);
                setDeleteMode("single");
                onOpen();
              }}
              title="Delete"
              onMouseDown={(e) => e.preventDefault()}
            >
              <Trash2 color="#787474" size={16} className="mt-1" />
            </button>
          </div>
        ),
      },
    ];

    setColumnDefs(createColumnDefs());
  }, []);

  const onSelectionChanged = () => {
    if (isOpen || !gridRef.current?.api) return;

    try {
      const selectedNodes = gridRef.current.api.getSelectedNodes() || [];
      const totalDisplayedRows = gridRef.current.api.getDisplayedRowCount() || 0;

      selectedIdsRef.current = selectedNodes.map((node) => node.data.privilegeId);
      setSelectedNames(selectedNodes.map((node) => `${node.data.roleName} - ${node.data.menuName}`));

      if (selectedNodes.length === totalDisplayedRows && totalDisplayedRows > 0) {
        setAllSelected(true);
        setDeleteMode("multi");
        onOpen();
      } else {
        setAllSelected(false);
      }
    } catch (error) {
      console.error("Error in onSelectionChanged:", error);
    }
  };

  const onOpen = () => {
    if (!gridRef.current?.api) return;

    try {
      const selectedNodes = gridRef.current.api.getSelectedNodes() || [];
      selectedIdsRef.current = selectedNodes.map((node) => node.data.privilegeId);
      setSelectedNames(selectedNodes.map((node) => `${node.data.roleName} - ${node.data.menuName}`));
      setPreserveSelection(true);
      setIsOpen(true);
    } catch (error) {
      console.error("Error in onOpen:", error);
    }
  };

  const onClose = () => {
    setIsOpen(false);
    setAllSelected(false);
    setPreserveSelection(false);
  };

  const confirmDelete = () => {
    setRowData((prev) => {
      if (deleteMode === "multi" || allSelected) {
        return prev.filter((row) => !selectedIdsRef.current.includes(row.privilegeId));
      } else {
        return prev.filter((row) => row.privilegeId !== deleteId);
      }
    });
    toast.success("Role privilege deleted successfully");
    onClose();
  };

  const paginatedData = rowData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  useEffect(() => {
    const totalPages = Math.ceil(rowData.length / itemsPerPage);
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [rowData, currentPage, itemsPerPage]);

  useEffect(() => {
    if (preserveSelection && selectedIdsRef.current.length > 0 && gridRef.current?.api) {
      try {
        gridRef.current.api.forEachNode((node) => {
          if (selectedIdsRef.current.includes(node.data.privilegeId)) {
            node.setSelected(true);
          }
        });
      } catch (error) {
        console.error("Error in preserveSelection effect:", error);
      }
    }
  }, [rowData, currentPage, preserveSelection]);

  return (
    <div className="container p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-brandRed">Role Privilege List</h2>
        <button
          className="px-4 py-2 addListTable-button"
          onClick={() => navigate("/role-privilege/add")}
        >
          Add
        </button>
      </div>

      <ListView
        filterColumn={filterColumn}
        setFilterColumn={setFilterColumn}
        columnOptions={columnOptions}
        filterValue={filterValue}
        setFilterValue={setFilterValue}
        handleFilter={handleFilter}
        clearFilters={clearFilters}
        isFilterApplied={isFilterApplied}
        gridRef={gridRef}
        columnDefs={columnDefs}
        rowData={rowData}
        paginatedData={paginatedData}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        onSelectionChanged={onSelectionChanged}
        defaultColDef={{ sortable: true, resizable: true, suppressMenu: true }}
        loading={loading}
        handleItemsPerPageChange={handleItemsPerPageChange}
      />

      <ConfirmModal
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={confirmDelete}
        title="Confirm Deletion"
        message={
          allSelected
            ? `Are you sure you want to delete all ${paginatedData.length} role privileges on this page?`
            : deleteMode === "multi"
            ? `Are you sure you want to delete ${selectedIdsRef.current.length} selected role privileges?`
            : "Are you sure you want to delete this role privilege?"
        }
        selectedNames={selectedNames}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmColor="backButton"
      />
    </div>
  );
};

export default RolePrivilege;
